import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Alert, Space, Tabs, Divider } from 'antd';
import { UserOutlined, LockOutlined, HomeOutlined, SoundOutlined, WechatOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import WeChatQRLogin from '../components/WeChatQRLogin';
import axios from 'axios';
import '../assets/css/Login.css';

const { Title } = Typography;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedReturn = location.state?.returnTo;
  const returnTo = /^\/school-admin-activation#token=[a-f0-9]{64}$/.test(requestedReturn || '') ? requestedReturn : '/dashboard';
  const { login, isAuthenticated, setUserLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form]=Form.useForm();
  const [captcha,setCaptcha]=useState(null);
  const [captchaLoading,setCaptchaLoading]=useState(false);
  const refreshCaptcha=async(username=form.getFieldValue('username'))=>{
    if(!username)return;
    setCaptchaLoading(true);setCaptcha(null);form.setFieldValue('captchaAnswer','');
    try {
      const response=await axios.get('/api/v2/auth/captcha',{params:{username}});
      if(username===form.getFieldValue('username'))setCaptcha({...response.data.data,username});
    }catch(err){setError(err.response?.data?.message || '图形验证码暂时不可用，请稍后重试');}
    finally{setCaptchaLoading(false);}
  };

  // 检测认证状态变化
  useEffect(() => {
    if (isAuthenticated) {
      // 设置一个短暂的延迟，确保状态完全更新
      const timer = setTimeout(() => {
        navigate(returnTo, { replace: true });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, navigate, returnTo]);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      setError(null);
      const result = await login(values.username,values.password,captcha && captcha.username===values.username ? {captchaId:captcha.captchaId,captchaAnswer:values.captchaAnswer} : {});

      if (result.success) {
        message.success('登录成功，欢迎回来！');
        // 不再直接跳转，而是等待useEffect检测到认证状态变化后跳转
      } else {
        setError(result.message || '登录失败，请稍后重试');
        if(result.captchaRequired || captcha)await refreshCaptcha(values.username);
      }
    } catch (err) {
      console.error('登录异常:', err);
      setError('登录过程中发生错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理微信扫码登录成功
  const handleWeChatLoginSuccess = (user) => {
    if (!user || !user.token) {
      message.error('登录信息不完整，请重试');
      return;
    }

    // 构建用户数据对象
    const userData = {
      id: user.id,
      username: user.username,
      name: user.name,
      role: user.role || 'teacher',
      userType: user.userType || 'teacher',
      schoolId: user.schoolId,
      teacherId: user.teacherId,
      teacherName: user.teacherName,
      schoolName: user.schoolName,
      subject: user.subject
    };

    // 使用AuthContext的setUserLogin方法更新状态
    setUserLogin(userData, user.token);
    message.success('微信扫码登录成功！');

    // 不需要手动跳转，useEffect会监听isAuthenticated变化自动跳转
  };

  return (
    <div className="login-container">
      <Card className="login-card" style={{ maxWidth: '900px', width: '100%' }}>
        <div className="login-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
            <SoundOutlined style={{ fontSize: '32px', color: '#1890ff', marginRight: '12px' }} />
            <Title level={2} className="login-title" style={{ margin: 0 }}>教室小喇叭</Title>
          </div>
          <p className="login-subtitle">用户登录</p>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <Button
              type="link"
              icon={<HomeOutlined />}
              onClick={() => navigate('/')}
              style={{ color: '#1890ff' }}
            >
              返回首页
            </Button>
          </div>
        </div>

        <Tabs
          centered
          defaultActiveKey="account"
          items={[
            {
              key: 'account',
              label: (
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  账号密码登录
                </span>
              ),
              children: (
                <div style={{ padding: '20px 0', maxWidth: '400px', margin: '0 auto' }}>
                  {error && (
                    <Alert
                      message="登录失败"
                      description={error}
                      type="error"
                      showIcon
                      style={{ marginBottom: 24 }}
                    />
                  )}

                  <Form
                    form={form}
                    name="login"
                    initialValues={{ remember: true }}
                    onFinish={onFinish}
                    size="large"
                  >
                    <Form.Item
                      name="username"
                      rules={[{ required: true, message: '请输入用户名' }]}
                    >
                      <Input
                        prefix={<UserOutlined />}
                        placeholder="用户名"
                        autoComplete="username"
                        onChange={()=>{setCaptcha(null);form.setFieldValue('captchaAnswer','');}}
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      rules={[{ required: true, message: '请输入密码' }]}
                    >
                      <Input.Password
                        prefix={<LockOutlined />}
                        placeholder="密码"
                        autoComplete="current-password"
                      />
                    </Form.Item>

                    {captcha && <>
                      <Space style={{marginBottom:12}}>
                        <img src={captcha.image} alt="图形验证码" width="160" height="52" />
                        <Button onClick={()=>refreshCaptcha()} loading={captchaLoading}>换一张</Button>
                      </Space>
                      <Form.Item name="captchaAnswer" rules={[{required:true,message:'请输入图形验证码'}]}>
                        <Input placeholder="图形验证码（不区分大小写）" autoComplete="off" maxLength={5} />
                      </Form.Item>
                    </>}
                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading || captchaLoading}
                        block
                      >
                        登录
                      </Button>
                    </Form.Item>
                  </Form>
                </div>
              )
            },
            {
              key: 'wechat',
              label: (
                <span>
                  <WechatOutlined style={{ marginRight: 8, color: '#07c160' }} />
                  微信扫码登录
                </span>
              ),
              children: (
                <div style={{ padding: '12px 0 8px' }}>
                  <WeChatQRLogin
                    onSuccess={handleWeChatLoginSuccess}
                    onError={(error) => {
                      console.error('微信登录错误:', error);
                      message.error('微信登录失败，请稍后重试');
                    }}
                  />

                  <Alert
                    message="隐私保护声明"
                    description={
                      <div>
                        本网站郑重承诺：不收集任何个人信息。仅使用微信扫码验证身份，安全可靠。
                        <br />
                        <a href="/privacy.html" target="_blank" style={{ textDecoration: 'underline' }}>查看完整隐私政策</a>
                      </div>
                    }
                    type="success"
                    showIcon
                    style={{ marginTop: 20, maxWidth: 720, marginLeft: 'auto', marginRight: 'auto' }}
                  />

                  <div style={{ textAlign: 'center', marginTop: 20, fontSize: '12px', color: '#999' }}>
                    <a href="/privacy.html" target="_blank" style={{ color: '#999' }}>隐私政策</a>
                    <Divider type="vertical" />
                    <span>仅限教师使用微信扫码登录</span>
                  </div>
                </div>
              )
            }
          ]}
        />
        <Divider />
        <div style={{ textAlign: 'center' }}>
          <Space wrap>
            <span style={{ color: '#666' }}>还没有账号？</span>
            <Button type="link" icon={<UserOutlined />} onClick={() => navigate('/register')}>
              注册新账号
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default Login; 
